import { baseUrl, httpClient } from '../providers/AppDataProvider';

const API_PATH = `${baseUrl}/vault/vaults`;

/** C-06 `VaultState`. */
export type VaultState = 'DRAFT' | 'RAISING' | 'ACTIVE' | 'PAUSED' | 'CLOSING' | 'CLOSED';

/** C-06 `VaultClosureState` (XBIT-4243). */
export type VaultClosureState =
  | 'AWAITING_APPROVAL'
  | 'NOTICE'
  | 'REQUESTED'
  | 'FROZEN'
  | 'SETTLED'
  | 'ABORTED';

/**
 * C-06 `VaultClosure` (XBIT-4243): a vault's closure by operations. `approvers` are the distinct operators, the
 * requester first; two are needed. `freezeAt` is the end of the notice (RFC 3339), null before the second approval.
 */
export interface VaultClosure {
  id: string;
  vaultId: string;
  state: VaultClosureState;
  requestedBy: string;
  approvers: string[];
  /**
   * The BFF's usernames beside the ids above (the raw id when no user matches); absent from an older BFF. The console
   * shows these and compares the ids, never the names.
   */
  requestedByName?: string;
  approverNames?: string[];
  cancelledByName?: string | null;
  reason: string;
  freezeAt: string | null;
  /** Set when the vault moves to CLOSING; the state an ABORTED from REQUESTED or FROZEN restores. */
  priorState: VaultState | null;
  cancelledBy: string | null;
  createdAt: string;
}

/**
 * C-06 `VaultValuation` (XBIT-4238): the FR-13 capacity set from the latest persisted cut-off.
 * Amounts are 6-dp strings; `utilization` is a FRACTION (`"0.400000"` = 40%), null when the cap is zero.
 */
export interface VaultValuation {
  valuedAt: string;
  totalAssets: string;
  totalShares: string;
  nav: string;
  managerShares: string;
  /** C-03 managerRatio at the cut-off (XBIT-4239): manager shares ÷ all shares, a FRACTION; absent from an older BFF. */
  managerRatio?: string;
  managerPresentValue: string;
  sizeCap: string;
  remainingCapacity: string;
  utilization: string | null;
}

/** C-06 `Vault`. Ratios are 6-dp FRACTION strings. `valuation` is null before the vault's first cut-off. */
export interface Vault {
  vaultId: string;
  name: string;
  kind: 'OFFICIAL' | 'COMMUNITY';
  state: VaultState;
  strategy: string;
  managerName: string;
  managerAddress: string;
  managerHolderId: string;
  /** The vault account's own HyperCore address (C-06 hlAddress), never the manager's; absent from an older BFF. */
  hlAddress?: string;
  /** T0..T3, or "" for none (the official vault). */
  tier: string;
  feeRate: string;
  lockupDays: number;
  ownershipFloor: string;
  /**
   * The floor the engine enforces: max(ownershipFloor, the tier's stake requirement) (C-06 / C-03 effectiveFloor,
   * XBIT-4373); absent from an older BFF.
   */
  effectiveFloor?: string;
  redemptionMethod: string;
  createdAt: string;
  raisingStartedAt: string | null;
  raisingInceptionAt: string | null;
  holderCount: number;
  valuation: VaultValuation | null;
  /** XBIT-4242: who paused new subscriptions, set exactly while PAUSED (C-06); absent from an older BFF. */
  subscriptionsPausedBy?: PauseSource | null;
  /** XBIT-4401: the vault's X handle and group chat from the catalog, as the app shows them; absent from an older BFF. */
  xHandle?: string | null;
  groupChatId?: string | null;
  /** XBIT-4243: the vault's live closure (any state but SETTLED or ABORTED); null when none; absent from an older BFF. */
  closure?: VaultClosure | null;
}

/** Who paused a vault's subscriptions: its manager, or operations (C-06 / C-03 PauseSource). */
export type PauseSource = 'MANAGER' | 'OPERATIONS';

/** What a pause or resume left (C-06 VaultSubscriptionsChange). applied=false: a replay that changed nothing. */
export interface VaultSubscriptionsChange {
  vaultId: string;
  state: VaultState;
  subscriptionsPausedBy: PauseSource | null;
  applied: boolean;
  reason: string | null;
  changedAt: string | null;
}

/** `POST /vault/vaults/:id/pause` (XBIT-4242): operations pause subscriptions; redemptions stay open. */
export async function pauseVaultSubscriptions(
  vaultId: string,
  reason: string
): Promise<VaultSubscriptionsChange> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/pause`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return json as VaultSubscriptionsChange;
}

/** `POST /vault/vaults/:id/resume` (XBIT-4242): operations resume subscriptions. */
export async function resumeVaultSubscriptions(vaultId: string): Promise<VaultSubscriptionsChange> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/resume`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return json as VaultSubscriptionsChange;
}

/** What a manager address change left (C-06 VaultManagerAddressChange). applied=false: the address was already the manager's. */
export interface VaultManagerAddressChange {
  vaultId: string;
  managerAddress: string;
  previousAddress: string;
  applied: boolean;
  changedAt: string | null;
}

/**
 * `POST /vault/vaults/:id/manager-address` (XBIT-4258): operations change an OFFICIAL vault's manager address to another
 * wallet of the same manager. It changes the address only: not the UID, and no Hyperliquid trading permission moves.
 */
export async function changeVaultManagerAddress(
  vaultId: string,
  address: string,
  reason: string
): Promise<VaultManagerAddressChange> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/manager-address`, {
    method: 'POST',
    body: JSON.stringify({ address, reason }),
  });
  return json as VaultManagerAddressChange;
}

/** `GET /vault/vaults/closure-available` (XBIT-4243): false while closing a vault is shipped dark. */
export async function getVaultClosureAvailable(): Promise<boolean> {
  const { json } = await httpClient(`${API_PATH}/closure-available`, { method: 'GET' });
  return Boolean((json as { available?: unknown })?.available);
}

/** `POST /vault/vaults/:id/closure` (XBIT-4243): an operator asks to close the vault; AWAITING_APPROVAL, the vault untouched. */
export async function requestVaultClosure(vaultId: string, reason: string): Promise<VaultClosure> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/closure`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return json as VaultClosure;
}

/** `POST /vault/vaults/:id/closure/approve` (XBIT-4243): a second distinct operator's approval starts the notice. */
export async function approveVaultClosure(
  vaultId: string,
  closureId: string
): Promise<VaultClosure> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/closure/approve`, {
    method: 'POST',
    body: JSON.stringify({ closureId }),
  });
  return json as VaultClosure;
}

/** `POST /vault/vaults/:id/closure/cancel` (XBIT-4243): one operator cancels while AWAITING_APPROVAL or NOTICE. */
export async function cancelVaultClosure(
  vaultId: string,
  closureId: string
): Promise<VaultClosure> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/closure/cancel`, {
    method: 'POST',
    body: JSON.stringify({ closureId }),
  });
  return json as VaultClosure;
}

/** The links a contact edit sets (C-06 SetVaultContactInput): both every time, null to clear. */
export interface VaultContact {
  xHandle: string | null;
  groupChatId: string | null;
}

/** What a contact edit left (C-06 VaultContactChange). applied=false: a replay that changed nothing. */
export interface VaultContactChange extends VaultContact {
  vaultId: string;
  applied: boolean;
  changedAt: string | null;
}

/** `POST /vault/vaults/:id/contact` (XBIT-4401): operations set a vault's X handle and group chat; a full replace. */
export async function setVaultContact(
  vaultId: string,
  contact: VaultContact
): Promise<VaultContactChange> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/contact`, {
    method: 'POST',
    body: JSON.stringify({ xHandle: contact.xHandle, groupChatId: contact.groupChatId }),
  });
  return json as VaultContactChange;
}

/** `GET /vault/vaults/:id` — one vault, the row the list carries (XBIT-4239). An unknown id is 404 VAULT_NOT_FOUND. */
export async function getVault(vaultId: string): Promise<Vault> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}`, {
    method: 'GET',
  });
  return json as Vault;
}

/** C-06 `VaultKind`. */
export type VaultKind = 'OFFICIAL' | 'COMMUNITY';

/** C-06 `VaultDisplayOrder` (XBIT-4247): a kind's console-set order and the revision the next reorder presents. */
export interface VaultDisplayOrder {
  kind: VaultKind;
  revision: number;
  /** The positioned vaults, first to last; the others of the kind are unset and listed after them. */
  orderedVaultIds: string[];
}

/** `GET /vault/vaults/order?kind=` (XBIT-4247, Module PRD §14.4 Ordering): a kind's order and revision. */
export async function getVaultDisplayOrder(kind: VaultKind): Promise<VaultDisplayOrder> {
  const { json } = await httpClient(`${API_PATH}/order?kind=${encodeURIComponent(kind)}`, {
    method: 'GET',
  });
  return json as VaultDisplayOrder;
}

/**
 * `POST /vault/vaults/order` (XBIT-4247): replace one kind's whole order under the revision last read. A vault left out
 * of the list becomes unset. A stale revision is 409 VAULT_ORDER_CONFLICT; nothing is written.
 */
export async function reorderVaults(
  kind: VaultKind,
  orderedVaultIds: string[],
  expectedRevision: number
): Promise<VaultDisplayOrder> {
  const { json } = await httpClient(`${API_PATH}/order`, {
    method: 'POST',
    body: JSON.stringify({ kind, orderedVaultIds, expectedRevision }),
  });
  return json as VaultDisplayOrder;
}

/** `GET /vault/vaults` — the engine orders it (official first). */
export async function listVaults(): Promise<Vault[]> {
  const { json } = await httpClient(API_PATH, { method: 'GET' });
  return Array.isArray(json) ? (json as Vault[]) : [];
}

/** C-06 `HolderLockupStatus`. STAKE_LOCKED is the manager's stake (D-05): locked until the vault closes. */
export type HolderLockupStatus = 'UNLOCKED' | 'LOCKED' | 'STAKE_LOCKED';

/**
 * C-06 `VaultHolder` (XBIT-4278, PRD §14.5). Amounts are 6-dp strings. `value` and `cumulativePnl` are null before
 * the vault's first cut-off; `cumulativePnl` can be negative. `unlockAt` is the latest calendar lock still ahead:
 * with LOCKED, and with STAKE_LOCKED when the manager also holds a locked top-up.
 */
export interface VaultHolder {
  holderId: string;
  /** Sender of the holder's latest attributed transfer in; null until one exists. */
  address: string | null;
  isManager: boolean;
  shares: string;
  value: string | null;
  highWaterMarkNav: string;
  avgCostNav: string;
  cumulativePnl: string | null;
  lockupStatus: HolderLockupStatus;
  unlockAt: string | null;
}

/** C-06 `VaultHolderPage`: one page, the manager first; `total` / `totalShares` cover the whole vault. */
export interface VaultHolderPage {
  vaultId: string;
  valuedAt: string | null;
  nav: string | null;
  total: number;
  totalShares: string;
  items: VaultHolder[];
}

/** C-06 `VaultRiskLimit` (XBIT-4741): the five platform risk limits (Module PRD §14.7 Risk). */
export type VaultRiskLimit =
  | 'INSTRUMENT_WHITELIST'
  | 'MAX_LEVERAGE'
  | 'CONCENTRATION'
  | 'MARGIN_WARNING'
  | 'FORCED_DELEVERAGE';

/**
 * C-06 `VaultRiskBreach` (XBIT-4741, XBIT-4764): one breach episode of the after-fill risk monitor. It opens on the first
 * check that finds a limit exceeded and closes on the check that ends a clear hold; `closedAt` is null while it is open.
 * `valueAtOpen` / `limitAtOpen` are 6 dp decimal strings (leverage as a multiple, the others as fractions); `limitAtOpen`
 * is null for INSTRUMENT_WHITELIST, whose `detail` names the coins. Times are RFC 3339 UTC.
 */
export interface VaultRiskBreach {
  id: string;
  vaultId: string;
  limit: VaultRiskLimit;
  openedAt: string;
  closedAt: string | null;
  valueAtOpen: string;
  limitAtOpen: string | null;
  detail: string | null;
}

export interface VaultRiskBreachPage {
  total: number;
  items: VaultRiskBreach[];
}

/**
 * `GET /vault/vaults/:id/risk-breaches` — `open: true` only the episodes still open; `open: false` or omitted EVERY
 * episode, open and closed (C-06). Newest first. offset ≥ 0, 1 ≤ limit ≤ 200. A 503 `VAULT_RISK_BREACHES_UNAVAILABLE`
 * means the engine has no breach reader on this stage: the console says "unavailable", never "0".
 */
export async function listVaultRiskBreaches(
  vaultId: string,
  params: { open?: boolean; offset: number; limit: number }
): Promise<VaultRiskBreachPage> {
  const query = new URLSearchParams({ offset: String(params.offset), limit: String(params.limit) });
  if (params.open !== undefined) query.set('open', String(params.open));
  const { json } = await httpClient(
    `${API_PATH}/${encodeURIComponent(vaultId)}/risk-breaches?${query}`,
    { method: 'GET' }
  );
  return json as VaultRiskBreachPage;
}

/** `GET /vault/vaults/:id/holders` — offset ≥ 0, 1 ≤ limit ≤ 200 (the BFF and the engine both refuse otherwise). */
export async function listVaultHolders(
  vaultId: string,
  page: { offset: number; limit: number }
): Promise<VaultHolderPage> {
  const query = new URLSearchParams({ offset: String(page.offset), limit: String(page.limit) });
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/holders?${query}`, {
    method: 'GET',
  });
  return json as VaultHolderPage;
}

// ---- §14.6 Subscriptions & redemptions records (XBIT-4245) ----

const RECORDS_PATH = `${baseUrl}/vault/records`;

/** C-06 `SubscriptionRecordStatus`: a transfer in the runner decided. A parked or quarantined transfer is not a record. */
export type SubscriptionRecordStatus = 'SUBSCRIBED' | 'REJECTED';

/** C-06 `ClosureStatus` (the client's C-03 enum). PRD §14.6: Paid = EXECUTED, Closing = ISSUED | CLOSING. */
export type ClosureStatus = 'ISSUED' | 'CLOSING' | 'EXECUTED' | 'ROLLED_BACK' | 'ESCALATED';

/** C-06 `SubscriptionRejection`: the blocker the runner recorded and the refund it owes. */
export interface SubscriptionRejection {
  /** The C-03 ActionBlocker code (WITHIN_LOCKUP, ABOVE_SIZE_CAP, …). */
  blocker: string;
  refundBasis: string;
  /** The refund has left; the record stays REJECTED. */
  refunded: boolean;
  rejectedAt: string;
}

/**
 * C-06 `SubscriptionRecord`. Amounts are 6-dp strings, times RFC 3339 UTC. The fill (`sharesIssued`, `navExecuted`,
 * `executedAt`) exists only when SUBSCRIBED; `rejection` only when REJECTED.
 */
export interface SubscriptionRecord {
  /** The transfer id — the transfer is the order. */
  id: string;
  vaultId: string;
  vaultName: string;
  holderId: string;
  /** The sender. */
  address: string;
  txHash: string;
  amount: string;
  arrivedAt: string;
  status: SubscriptionRecordStatus;
  sharesIssued: string | null;
  navExecuted: string | null;
  executedAt: string | null;
  /** The manager's stake (D-05), tagged "Manager stake". */
  isManagerStake: boolean;
  rejection: SubscriptionRejection | null;
}

export interface SubscriptionRecordPage {
  total: number;
  items: SubscriptionRecord[];
}

/**
 * C-06 `RedemptionRecord`: a closure instruction. The settled figures (`recovered`, `slippage`, `fee`, `net`,
 * `executedAt`) exist only at EXECUTED; the reasons only at ROLLED_BACK / ESCALATED.
 */
export interface RedemptionRecord {
  /** The closure instruction id — the order. */
  id: string;
  /** The holder's own order id; null when no receipt names one. */
  clientOrderId: string | null;
  vaultId: string;
  vaultName: string;
  holderId: string;
  requested: string;
  /** The close ratio x = s_r ÷ S, as issued (a fraction). */
  fractionX: string;
  sharesToBurn: string;
  navAtIssue: string;
  status: ClosureStatus;
  issuedAt: string;
  recovered: string | null;
  slippage: string | null;
  /** Fee → manager, the journal FEE entry. */
  fee: string | null;
  /**
   * XBIT-4311: the part of the fee fixed at submit that the cap at paid gave up; `fee + feeForgone` is that submit
   * fee, and it is 0 unless the cap bit (D-23 amendment 1). Not derivable from the figures beside it.
   */
  feeForgone: string | null;
  /** Net to user, the payout leg. */
  net: string | null;
  executedAt: string | null;
  failureReason: string | null;
  escalationReason: string | null;
}

export interface RedemptionRecordPage {
  total: number;
  items: RedemptionRecord[];
}

/** The BFF's query: set filters only; `from` / `to` come together (RFC 3339, inclusive); 0 ≤ offset, 1 ≤ limit ≤ 200. */
export interface RecordsQuery {
  vaultId?: string;
  holderId?: string;
  status?: string;
  from?: string;
  to?: string;
  offset: number;
  limit: number;
}

const recordsSearch = (query: RecordsQuery): string => {
  const params = new URLSearchParams();
  (Object.keys(query) as (keyof RecordsQuery)[]).forEach((key) => {
    const value = query[key];
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  return params.toString();
};

/** `GET /vault/records/subscriptions` — newest arrival first. */
export async function listSubscriptionRecords(
  query: RecordsQuery
): Promise<SubscriptionRecordPage> {
  const { json } = await httpClient(`${RECORDS_PATH}/subscriptions?${recordsSearch(query)}`, {
    method: 'GET',
  });
  return json as SubscriptionRecordPage;
}

/** `GET /vault/records/redemptions` — newest issue first. */
export async function listRedemptionRecords(query: RecordsQuery): Promise<RedemptionRecordPage> {
  const { json } = await httpClient(`${RECORDS_PATH}/redemptions?${recordsSearch(query)}`, {
    method: 'GET',
  });
  return json as RedemptionRecordPage;
}

// ---- XBIT-4709: treasury work orders waiting on a person ----

const APPROVALS_PATH = `${baseUrl}/vault/approvals`;

/** The BFF's `TreasuryAction`: a work order a person has to act on. `amount` is a string; the ids are null where they do not apply. */
export interface ApprovalRow {
  workOrderId: string;
  lane: string;
  status: string;
  amount: string;
  destination: string;
  vaultId: string | null;
  redemptionRecordId: string | null;
  /** Null until the engine has recorded the Turnkey activity (it does so after the request reaches Turnkey). */
  activityId: string | null;
  activityLive: boolean;
  stuckReason: string | null;
  /** Null when the order never recorded one; the waiting time then runs from `createdAt`. */
  waitingSince: string | null;
  createdAt: string;
  /**
   * XBIT-3988: every source holding this order right now, joined with `"; "`, or null when nothing does.
   *
   * A string rather than an enum because BOTH a reconciliation freeze and an operator lane pause can hold one order at
   * once, and an operator who clears the freeze must still see the pause — otherwise they clear one, find the money
   * still stopped, and go looking for a third cause that is not there. `"(unreadable)"` names a source that could not
   * be read, which is not the same as nothing holding the order. Null on any status a hold can no longer stop.
   */
  heldBy?: string | null;
}

export interface ApprovalPage {
  total: number;
  items: ApprovalRow[];
}

/**
 * C-06 `TreasuryActionOutcome`, which groups by what moves an order on rather than by status.
 *
 * `IN_FLIGHT` is the only group a HELD order at SIGNED or AWAITING_FLOAT appears in: a hold keeps the order's status
 * and writes no transition, so one sits there looking idle rather than queued anywhere. Omitting the outcome keeps the
 * BFF's default answer, the waiting queue.
 */
export type TreasuryActionOutcome = 'WAITING' | 'IN_FLIGHT' | 'RESOLVED';

/** `GET /vault/approvals` — longest-waiting first; 0 ≤ offset, 1 ≤ limit ≤ 200. */
export async function listApprovals(
  page: { offset: number; limit: number },
  outcome?: TreasuryActionOutcome,
): Promise<ApprovalPage> {
  const query = new URLSearchParams({ offset: String(page.offset), limit: String(page.limit) });
  if (outcome) query.set('outcome', outcome);
  const { json } = await httpClient(`${APPROVALS_PATH}?${query}`, { method: 'GET' });
  return json as ApprovalPage;
}

// ---- XBIT-4641: the audit log's engine-only actions (Module PRD §14.8) ----

const AUDIT_PATH = `${baseUrl}/vault/audit`;

/** C-06 `ClosureResolutionPath`, in schema order. */
export type ClosureResolutionPath = 'SETTLE' | 'RELEASE';

/**
 * C-06 `ClosureResolution`: an ESCALATED redemption an operator resolved from the engine CLI. Amounts are 6-dp strings,
 * times RFC 3339 UTC. There is no role: a CLI action has no console role, the two approvers are its evidence.
 */
export interface ClosureResolution {
  /** 对象: the redemption record id (the closure instruction id). */
  redemptionId: string;
  vaultId: string;
  vaultName: string;
  resolvedAt: string;
  operator: string;
  approvers: string[];
  path: ClosureResolutionPath;
  /** 修改前: always ESCALATED. */
  statusBefore: ClosureStatus;
  /** 修改后: EXECUTED for SETTLE, ROLLED_BACK for RELEASE. */
  statusAfter: ClosureStatus;
  recovered: string;
  closedFraction: string;
  /** RELEASE only; null for SETTLE. */
  slippage: string | null;
  note: string;
}

export interface ClosureResolutionPage {
  total: number;
  items: ClosureResolution[];
}

/** The BFF's query: 0 ≤ offset, 1 ≤ limit ≤ 200; `vaultId` narrows to one vault. */
export interface ClosureResolutionsQuery {
  vaultId?: string;
  offset: number;
  limit: number;
}

/** `GET /vault/audit/closure-resolutions` — newest resolution first. */
export async function listClosureResolutions(
  query: ClosureResolutionsQuery
): Promise<ClosureResolutionPage> {
  const params = new URLSearchParams();
  if (query.vaultId) params.set('vaultId', query.vaultId);
  params.set('offset', String(query.offset));
  params.set('limit', String(query.limit));
  const { json } = await httpClient(`${AUDIT_PATH}/closure-resolutions?${params.toString()}`, {
    method: 'GET',
  });
  return json as ClosureResolutionPage;
}

// ---- XBIT-4246: the dashboard (Module PRD §14.2) ----

const DASHBOARD_PATH = `${baseUrl}/vault/dashboard`;

/** C-06 `PlatformStats`: the four cards, for "today" in `timeZone`. Amounts are 6-dp strings. */
export interface PlatformStats {
  /** RFC 3339 full-date: today in `timeZone`. */
  day: string;
  /** The zone the engine used (an IANA name or ±HH:MM); +09:00 when it did not understand the one sent. */
  timeZone: string;
  dayStart: string;
  dayEnd: string;
  totalValueLocked: string;
  /** The oldest cut-off summed; null when none. */
  valuedAt: string | null;
  /** Vaults neither DRAFT nor CLOSED with no cut-off yet: left out of `totalValueLocked`. */
  unvaluedVaultCount: number;
  holderCount: number;
  /** Signed: negative when more left than came in. */
  netSubscriptions: string;
  managerFees: string;
}

export interface PlatformSeriesDay {
  date: string;
  /** Null on a day no vault had a cut-off: a gap, never 0. */
  holderCount: number | null;
  managerFees: string;
}

export interface VaultNavPoint {
  date: string;
  /** Null on a day the vault had no cut-off: a gap, never 0. */
  nav: string | null;
}

export interface VaultNavSeries {
  vaultId: string;
  name: string;
  kind: Vault['kind'];
  points: VaultNavPoint[];
}

/** C-06 `PlatformSeries`: `days` oldest first, today last; one NAV line per non-DRAFT vault, official first. */
export interface PlatformSeries {
  timeZone: string;
  days: PlatformSeriesDay[];
  vaultNav: VaultNavSeries[];
}

/** `GET /vault/dashboard`. `timeZone` is sent URL-encoded: a "+" in an offset must not reach the BFF as a space. */
export async function getPlatformStats(timeZone?: string): Promise<PlatformStats> {
  const query = timeZone ? `?timeZone=${encodeURIComponent(timeZone)}` : '';
  const { json } = await httpClient(`${DASHBOARD_PATH}${query}`, { method: 'GET' });
  return json as PlatformStats;
}

/** `GET /vault/dashboard/series`. */
export async function getPlatformSeries(days: number, timeZone?: string): Promise<PlatformSeries> {
  const params = new URLSearchParams({ days: String(days) });
  if (timeZone) params.set('timeZone', timeZone);
  const { json } = await httpClient(`${DASHBOARD_PATH}/series?${params.toString()}`, {
    method: 'GET',
  });
  return json as PlatformSeries;
}

// ---- XBIT-4864: reconciliation-freeze actions (BFF XBIT-4863, engine XBIT-4678) ----

/** C-06 `ShareAdjustmentStatus`, in schema order. */
export type ShareAdjustmentStatus = 'AWAITING_APPROVAL' | 'APPLIED' | 'CANCELLED';

/** C-06 `ShareAdjustmentProposal`. Amounts are 6 dp strings, times RFC 3339 UTC; the operators are admin ids. */
export interface ShareAdjustmentProposal {
  id: string;
  vaultId: string;
  /** Null for a share-neutral adjustment. */
  holderId: string | null;
  /** Signed decimal string. */
  deltaShares: string;
  navApplied: string | null;
  navValuedAt: string | null;
  reasonCode: string;
  memo: string;
  evidenceRef: string;
  proposedBy: string;
  proposedAt: string;
  status: ShareAdjustmentStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  /** The break it resolves; null for a pure correction. */
  breakValuedAt: string | null;
  breakAttemptSeq: number | null;
  appliedJournalSeq: number | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
}

/** C-06 `ReconciliationResolution`: why a break ended; `approvers` are the two distinct operators. */
export interface ReconciliationResolution {
  id: string;
  vaultId: string;
  breakValuedAt: string;
  breakAttemptSeq: number;
  cause: string;
  evidenceRef: string;
  approvers: string[];
  adjProposalId: string;
  resolvedAt: string;
}

export interface ProposeShareAdjustmentInput {
  /** Omitted (with a `deltaShares` of "0") for a share-neutral adjustment. */
  holderId?: string;
  deltaShares: string;
  reasonCode: string;
  memo: string;
  evidenceRef: string;
  /** A replay of the same key and body returns the first proposal. */
  idempotencyKey: string;
  breakValuedAt?: string | null;
  breakAttemptSeq?: number | null;
}

export interface ResolveBreakInput {
  breakValuedAt: string;
  breakAttemptSeq: number;
  cause: string;
  evidenceRef: string;
  proposalId: string;
}

/** C-06 `ReconciliationRunStatus`, in schema order. */
export type ReconciliationRunStatus = 'QUEUED' | 'RUNNING' | 'DEFERRED' | 'DONE' | 'FAILED';
export type ReconciliationRunVerdict = 'RECONCILED' | 'BROKEN' | 'UNRECONCILED';

export interface ReconciliationRunRequest {
  requestId: string;
  vaultId: string;
  status: ReconciliationRunStatus;
  requestedBy: string;
  requestedAt: string;
  /** FAILED: the refusal's `Reconciliation_*` code; QUEUED after a retry: the last transient failure. */
  errorCode: string | null;
  /** Set when DONE. */
  attempt: { triggeredAt: string; attemptSeq: number; verdict: ReconciliationRunVerdict } | null;
}

const reconciliationPath = (vaultId: string) =>
  `${API_PATH}/${encodeURIComponent(vaultId)}/reconciliation`;

const post = async <T>(url: string, body?: unknown): Promise<T> => {
  const { json } = await httpClient(url, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return json as T;
};

/** `GET …/reconciliation/adjustments` — newest first; `status` omitted = every status. */
export async function listShareAdjustments(
  vaultId: string,
  status?: ShareAdjustmentStatus
): Promise<ShareAdjustmentProposal[]> {
  const query = status ? `?${new URLSearchParams({ status })}` : '';
  const { json } = await httpClient(`${reconciliationPath(vaultId)}/adjustments${query}`, {
    method: 'GET',
  });
  return json as ShareAdjustmentProposal[];
}

/** `GET …/reconciliation/resolutions` — newest first. */
export async function listReconciliationResolutions(
  vaultId: string
): Promise<ReconciliationResolution[]> {
  const { json } = await httpClient(`${reconciliationPath(vaultId)}/resolutions`, {
    method: 'GET',
  });
  return json as ReconciliationResolution[];
}

/** `POST …/reconciliation/adjustments` — one operator proposes; no shares move until a second one approves. */
export const proposeShareAdjustment = (vaultId: string, input: ProposeShareAdjustmentInput) =>
  post<ShareAdjustmentProposal>(`${reconciliationPath(vaultId)}/adjustments`, input);

/** `POST …/adjustments/:proposalId/approve` (step-up) — the second, distinct operator journals the ADJ. */
export const approveShareAdjustment = (vaultId: string, proposalId: string) =>
  post<ShareAdjustmentProposal>(
    `${reconciliationPath(vaultId)}/adjustments/${encodeURIComponent(proposalId)}/approve`
  );

/** `POST …/adjustments/:proposalId/cancel` — withdraws an AWAITING_APPROVAL proposal. */
export const cancelShareAdjustment = (vaultId: string, proposalId: string) =>
  post<ShareAdjustmentProposal>(
    `${reconciliationPath(vaultId)}/adjustments/${encodeURIComponent(proposalId)}/cancel`
  );

/** `POST …/reconciliation/resolutions` (step-up) — approves `proposalId` AND resolves the break it names, in one transaction. */
export const resolveReconciliationBreak = (vaultId: string, input: ResolveBreakInput) =>
  post<ReconciliationResolution>(`${reconciliationPath(vaultId)}/resolutions`, input);

/** `POST …/reconciliation/runs` (step-up) — queues a run; resend the SAME `requestId` on a retry. */
export const runReconciliationNow = (vaultId: string, requestId: string) =>
  post<ReconciliationRunRequest>(`${reconciliationPath(vaultId)}/runs`, { requestId });

/** `GET …/reconciliation/run-requests/:requestId`. */
export async function getReconciliationRunRequest(
  vaultId: string,
  requestId: string
): Promise<ReconciliationRunRequest> {
  const { json } = await httpClient(
    `${reconciliationPath(vaultId)}/run-requests/${encodeURIComponent(requestId)}`,
    { method: 'GET' }
  );
  return json as ReconciliationRunRequest;
}

/** The vault's reconciliation freeze, as the round-3 read will answer it (BFF XBIT-4861). */
export interface ReconciliationFreeze {
  frozen: boolean;
  frozenAt: string | null;
  reason: string | null;
  breakValuedAt: string | null;
  breakAttemptSeq: number | null;
  runId: string | null;
}

/**
 * TODO(XBIT-4861, BFF round 2/3): `GET /vault/vaults/:id/reconciliation/freeze` (engine !479) does not exist yet. Until it
 * does this answers null ("unknown"), so no banner is shown and nothing is guessed; swap the body for the httpClient read
 * and the banner / Frozen vaults tab light up. Do NOT derive it from the closure's `freezeAt` / `FROZEN`: that is the
 * closure-notice freeze, a different thing.
 */
export async function getReconciliationFreeze(_vaultId: string): Promise<ReconciliationFreeze | null> {
  return null;
}
